import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const BrandContext = createContext({});

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

// Loads public appearance (brand + seo + geo) once and applies favicon, title and meta tags.
export function BrandProvider({ children }) {
  const [appearance, setAppearance] = useState(null);

  useEffect(() => {
    api.get("/public/appearance").then(({ data }) => setAppearance(data || {})).catch(() => setAppearance({}));
  }, []);

  useEffect(() => {
    if (!appearance) return;
    const brand = appearance.brand || {};
    const seo = appearance.seo || {};
    const geo = appearance.geo || {};

    if (brand.favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
      link.href = brand.favicon_url;
    }
    document.title = seo.title || brand.platform_name || document.title;
    setMeta("name", "description", seo.description || brand.meta_description);
    setMeta("name", "keywords", seo.keywords);
    setMeta("name", "robots", seo.robots);
    setMeta("property", "og:title", seo.title || brand.platform_name);
    setMeta("property", "og:description", seo.description || brand.meta_description);
    setMeta("property", "og:image", seo.og_image || brand.og_image);
    setMeta("name", "twitter:card", seo.twitter_card || "summary_large_image");
    // GEO meta (helps location-aware discovery)
    setMeta("name", "geo.region", geo.default_country);
    setMeta("name", "geo.placename", geo.business_region);
    if (geo.latitude && geo.longitude) setMeta("name", "geo.position", `${geo.latitude};${geo.longitude}`);
    if (document.documentElement && geo.default_language) document.documentElement.lang = geo.default_language;
  }, [appearance]);

  return <BrandContext.Provider value={(appearance || {}).brand || {}}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  return useContext(BrandContext) || {};
}
